(() => {
  if (window.__ymqImagePerformancePreflight) return;

  window.__ymqImagePerformancePreflight = true;

  const SWATCH_SELECTOR = 'img.ymq-option-swatches-image';
  const TOOLTIP_SELECTOR = 'img.ymq-option-tooltip-img';
  const GALLERY_ROOT_SELECTOR = '.lg-container, .lg-outer';
  const GALLERY_SLIDE_SELECTOR = '.lg-item';
  const GALLERY_THUMB_ITEM_SELECTOR = '.lg-thumb-item';
  const GALLERY_MAIN_SELECTOR = 'img.lg-object.lg-image';
  const GALLERY_THUMB_SELECTOR = `${GALLERY_THUMB_ITEM_SELECTOR} img`;
  const SELECTOR = [
    SWATCH_SELECTOR,
    TOOLTIP_SELECTOR,
    GALLERY_MAIN_SELECTOR,
    GALLERY_THUMB_SELECTOR
  ].join(', ');
  const MARKERS = [
    'ymq-option-swatches-image',
    'ymq-option-tooltip-img',
    'lg-item',
    'lg-thumb-item',
    'lg-object',
    'lg-image'
  ];
  const PROCESSED_ATTR = 'data-ymq-image-optimized';
  const GALLERY_SOURCE_ATTR = 'data-ymq-gallery-src';
  const FALLBACK_WIDTH = 240;
  const TOOLTIP_FALLBACK_WIDTH = 320;
  const GALLERY_FULL_WIDTH = 1200;
  const GALLERY_THUMB_WIDTH = 160;
  const WIDTHS = [160, 240, 320];
  const TRANSPARENT_PLACEHOLDER =
    'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';

  const nativeSetAttribute = Element.prototype.setAttribute;
  const nativeInsertAdjacentHTML = Element.prototype.insertAdjacentHTML;
  const nativeAppendChild = Node.prototype.appendChild;
  const nativeInsertBefore = Node.prototype.insertBefore;
  const nativeReplaceChild = Node.prototype.replaceChild;
  const innerHTMLDescriptor = Object.getOwnPropertyDescriptor(
    Element.prototype,
    'innerHTML'
  );
  const outerHTMLDescriptor = Object.getOwnPropertyDescriptor(
    Element.prototype,
    'outerHTML'
  );
  const imageSrcDescriptor = Object.getOwnPropertyDescriptor(
    HTMLImageElement.prototype,
    'src'
  );
  const nativeCreateContextualFragment =
    window.Range && Range.prototype.createContextualFragment;
  const nativeParseFromString =
    window.DOMParser && DOMParser.prototype.parseFromString;
  const nativeDocumentWrite = Document.prototype.write;
  const nativeDocumentWriteln = Document.prototype.writeln;

  let isPreparingImage = false;
  let isGalleryRefreshQueued = false;

  /**
   * 基于 Shopify CDN 原始地址生成指定宽度的图片地址。
   */
  function createImageUrl(source, width) {
    try {
      const url = new URL(source, window.location.origin);

      url.searchParams.set('width', String(width));
      url.searchParams.delete('height');

      return url.toString();
    } catch (error) {
      console.warn('[YMQ image optimization] Invalid image URL:', source);
      return source;
    }
  }

  function shouldSkipSource(source) {
    if (!source) return true;

    const normalized = String(source).trim().toLowerCase();

    if (
      normalized.startsWith('data:') ||
      normalized.startsWith('blob:') ||
      normalized.startsWith('mailto:') ||
      normalized.startsWith('tel:')
    ) {
      return true;
    }

    try {
      return new URL(source, window.location.origin).pathname
        .toLowerCase()
        .endsWith('.svg');
    } catch (error) {
      return normalized.includes('.svg');
    }
  }

  function isYmqImageSource(source) {
    if (!source || shouldSkipSource(source)) return false;

    try {
      const url = new URL(source, window.location.origin);

      return /ymq/i.test(url.pathname) || /ymq/i.test(url.search);
    } catch (error) {
      return /ymq/i.test(String(source));
    }
  }

  function isTargetImage(node) {
    return (
      node instanceof HTMLImageElement &&
      (node.matches(SWATCH_SELECTOR) ||
        node.matches(TOOLTIP_SELECTOR) ||
        isGalleryImage(node))
    );
  }

  function getFallbackWidth(img) {
    return img.matches(TOOLTIP_SELECTOR)
      ? TOOLTIP_FALLBACK_WIDTH
      : FALLBACK_WIDTH;
  }

  function getSizesValue(img) {
    return img.matches(TOOLTIP_SELECTOR) ? '160px' : '100px';
  }

  function setNativeAttribute(element, name, value) {
    nativeSetAttribute.call(element, name, value);
  }

  function setNativeAttributeIfChanged(element, name, value) {
    if (element.getAttribute(name) !== String(value)) {
      setNativeAttribute(element, name, value);
    }
  }

  function getPrimarySource(img) {
    return img.getAttribute('src') || img.getAttribute('data-src') || '';
  }

  function getGallerySource(img, fallbackSource) {
    return (
      img.getAttribute(GALLERY_SOURCE_ATTR) ||
      fallbackSource ||
      img.getAttribute('src') ||
      img.getAttribute('data-src') ||
      ''
    );
  }

  function getElementContext(element, selector) {
    if (!(element instanceof Element)) return null;

    if (element.matches(selector)) return element;

    return element.closest(selector);
  }

  function getGalleryKind(img, source, insertionParent) {
    if (!(img instanceof HTMLImageElement) || !isYmqImageSource(source)) {
      return null;
    }

    const thumbContext =
      getElementContext(img, GALLERY_THUMB_ITEM_SELECTOR) ||
      getElementContext(insertionParent, GALLERY_THUMB_ITEM_SELECTOR);

    if (thumbContext) return 'thumb';

    if (!img.matches(GALLERY_MAIN_SELECTOR)) return null;

    const mainContext =
      getElementContext(img, GALLERY_SLIDE_SELECTOR) ||
      getElementContext(img, GALLERY_ROOT_SELECTOR) ||
      getElementContext(insertionParent, GALLERY_SLIDE_SELECTOR) ||
      getElementContext(insertionParent, GALLERY_ROOT_SELECTOR);

    return mainContext ? 'main' : null;
  }

  function isGalleryImage(img) {
    return Boolean(getGalleryKind(img, getGallerySource(img)));
  }

  function shouldOptimizeSourceWrite(img, source) {
    return (
      img.matches(SWATCH_SELECTOR) ||
      img.matches(TOOLTIP_SELECTOR) ||
      Boolean(getGalleryKind(img, source))
    );
  }

  function getGallerySlideIndex(slide) {
    if (!(slide instanceof Element)) return null;

    const indexedImage = slide.querySelector('img[data-index]');
    const imageIndex = indexedImage && Number(indexedImage.dataset.index);

    if (Number.isFinite(imageIndex)) return imageIndex;

    const idMatch = String(slide.id || '').match(/lg-item-\d+-(\d+)$/);

    if (idMatch) return Number(idMatch[1]);

    return null;
  }

  function getGalleryItemCount(root) {
    const counter = root.querySelector('.lg-counter-all');
    const counterValue = counter && Number(counter.textContent);

    if (Number.isFinite(counterValue) && counterValue > 0) {
      return counterValue;
    }

    return root.querySelectorAll('.lg-thumb-item').length;
  }

  function shouldLoadGalleryMainImage(img, insertionParent) {
    const slide =
      getElementContext(img, GALLERY_SLIDE_SELECTOR) ||
      getElementContext(insertionParent, GALLERY_SLIDE_SELECTOR);

    if (!(slide instanceof Element)) return true;

    if (
      slide.classList.contains('lg-current') ||
      slide.classList.contains('lg-prev-slide') ||
      slide.classList.contains('lg-next-slide')
    ) {
      return true;
    }

    const root = slide.closest(GALLERY_ROOT_SELECTOR);
    const currentSlide = root && root.querySelector('.lg-item.lg-current');
    const currentIndex = getGallerySlideIndex(currentSlide);
    const slideIndex = getGallerySlideIndex(slide);
    const itemCount = root ? getGalleryItemCount(root) : 0;

    if (
      currentIndex === null ||
      slideIndex === null ||
      !Number.isFinite(itemCount) ||
      itemCount <= 0
    ) {
      return false;
    }

    const distance = Math.abs(slideIndex - currentIndex);
    const wrappedDistance = Math.min(distance, itemCount - distance);

    return wrappedDistance <= 1;
  }

  function getGallerySafeSource(img, source, galleryKind, insertionParent) {
    const fullSource = createImageUrl(source, GALLERY_FULL_WIDTH);
    const hasLoadedFullSource =
      img.getAttribute('src') === fullSource &&
      img.complete &&
      img.naturalWidth > 0;

    if (galleryKind === 'thumb') {
      return createImageUrl(source, GALLERY_THUMB_WIDTH);
    }

    if (galleryKind === 'main') {
      return hasLoadedFullSource ||
        shouldLoadGalleryMainImage(img, insertionParent)
        ? fullSource
        : TRANSPARENT_PLACEHOLDER;
    }

    return source;
  }

  function isGalleryImagePrepared(
    img,
    galleryKind,
    fullSource,
    nextSource,
    expectedLoading,
    expectedFetchPriority
  ) {
    const isMainImage = galleryKind === 'main';

    if (img.getAttribute(GALLERY_SOURCE_ATTR) !== fullSource) return false;
    if (img.getAttribute('src') !== nextSource) return false;
    if (img.getAttribute('loading') !== expectedLoading) return false;
    if (img.getAttribute('decoding') !== 'async') return false;
    if (img.getAttribute('fetchpriority') !== expectedFetchPriority) {
      return false;
    }
    if (img.getAttribute(PROCESSED_ATTR) !== 'true') return false;

    if (isMainImage) {
      if (nextSource === TRANSPARENT_PLACEHOLDER) {
        return (
          !img.hasAttribute('srcset') &&
          !img.hasAttribute('sizes') &&
          !img.hasAttribute('data-src')
        );
      }

      return (
        !img.hasAttribute('srcset') &&
        !img.hasAttribute('sizes') &&
        img.getAttribute('data-src') === fullSource
      );
    }

    const thumbSource = createImageUrl(fullSource, GALLERY_THUMB_WIDTH);

    return (
      img.getAttribute('srcset') === `${thumbSource} 160w` &&
      img.getAttribute('sizes') === '80px'
    );
  }

  function prepareGalleryImage(img, galleryKind, insertionParent) {
    const rawSource = getGallerySource(img);
    const kind = galleryKind || getGalleryKind(img, rawSource, insertionParent);

    if (!kind) return;

    const fullSource = createImageUrl(rawSource, GALLERY_FULL_WIDTH);
    const nextSource = getGallerySafeSource(
      img,
      rawSource,
      kind,
      insertionParent
    );
    const isMainImage = kind === 'main';
    const isCurrentImage =
      isMainImage &&
      img.closest('.lg-item') &&
      img.closest('.lg-item').classList.contains('lg-current');
    const shouldEagerLoad =
      isMainImage && shouldLoadGalleryMainImage(img, insertionParent);
    const expectedLoading = shouldEagerLoad ? 'eager' : 'lazy';
    const expectedFetchPriority = isCurrentImage ? 'high' : 'low';

    if (
      isGalleryImagePrepared(
        img,
        kind,
        fullSource,
        nextSource,
        expectedLoading,
        expectedFetchPriority
      )
    ) {
      return;
    }

    isPreparingImage = true;

    try {
      setNativeAttributeIfChanged(img, GALLERY_SOURCE_ATTR, fullSource);
      setNativeAttributeIfChanged(img, 'src', nextSource);

      if (isMainImage) {
        if (nextSource === TRANSPARENT_PLACEHOLDER) {
          if (img.hasAttribute('srcset')) img.removeAttribute('srcset');
          if (img.hasAttribute('sizes')) img.removeAttribute('sizes');
          if (img.hasAttribute('data-src')) img.removeAttribute('data-src');
        } else {
          if (img.hasAttribute('srcset')) img.removeAttribute('srcset');
          if (img.hasAttribute('sizes')) img.removeAttribute('sizes');
          setNativeAttributeIfChanged(img, 'data-src', fullSource);
        }
      } else {
        const thumbSource = createImageUrl(rawSource, GALLERY_THUMB_WIDTH);

        setNativeAttributeIfChanged(img, 'srcset', `${thumbSource} 160w`);
        setNativeAttributeIfChanged(img, 'sizes', '80px');
      }

      setNativeAttributeIfChanged(
        img,
        'loading',
        expectedLoading
      );
      setNativeAttributeIfChanged(img, 'decoding', 'async');
      setNativeAttributeIfChanged(
        img,
        'fetchpriority',
        expectedFetchPriority
      );
      setNativeAttributeIfChanged(img, PROCESSED_ATTR, 'true');
    } finally {
      isPreparingImage = false;
    }
  }

  function prepareImage(img) {
    if (!isTargetImage(img) || isPreparingImage) return;

    if (isGalleryImage(img)) {
      prepareGalleryImage(img);
      return;
    }

    const rawSource = getPrimarySource(img);

    if (shouldSkipSource(rawSource)) return;

    const sources = WIDTHS.reduce((result, width) => {
      result[width] = createImageUrl(rawSource, width);
      return result;
    }, {});

    isPreparingImage = true;

    try {
      setNativeAttribute(
        img,
        'srcset',
        `${sources[160]} 160w, ${sources[240]} 240w, ${sources[320]} 320w`
      );
      setNativeAttribute(img, 'sizes', getSizesValue(img));
      setNativeAttribute(img, 'src', sources[getFallbackWidth(img)]);

      /*
       * 保留 YMQ 原始 data-src。它不会直接触发下载，但 lightGallery
       * 会用它建立全屏图库并计算主图尺寸。实际缩略图请求仍由 src/srcset
       * 控制为 240/320px；后续若 YMQ 把 data-src 写回 src，也会被上方
       * 的 src 拦截逻辑重新缩小，因此不会重新产生普通 swatch 的 1200px 请求。
       */

      setNativeAttribute(img, 'loading', 'lazy');
      setNativeAttribute(img, 'decoding', 'async');
      setNativeAttribute(img, 'fetchpriority', 'low');

      setNativeAttribute(img, PROCESSED_ATTR, 'true');
    } finally {
      isPreparingImage = false;
    }
  }

  function prepareTree(node) {
    if (isTargetImage(node)) {
      prepareImage(node);
      return;
    }

    if (
      node instanceof Element ||
      node instanceof DocumentFragment ||
      node instanceof Document
    ) {
      node.querySelectorAll(SELECTOR).forEach(prepareImage);
    }
  }

  function prepareImageForParent(img, parent) {
    const rawSource = getGallerySource(img);
    const galleryKind = getGalleryKind(img, rawSource, parent);

    if (!galleryKind) return false;

    prepareGalleryImage(img, galleryKind, parent);
    return true;
  }

  function prepareTreeForParent(parent, node) {
    if (node instanceof HTMLImageElement && prepareImageForParent(node, parent)) {
      return;
    }

    prepareTree(node);
  }

  function rewriteMarkup(markup) {
    if (
      typeof markup !== 'string' ||
      !MARKERS.some((marker) => markup.indexOf(marker) !== -1)
    ) {
      return markup;
    }

    const template = document.createElement('template');

    innerHTMLDescriptor.set.call(template, markup);
    template.content.querySelectorAll(SELECTOR).forEach(prepareImage);

    return template.innerHTML;
  }

  /*
   * YMQ 会动态写入带 1200px src 的 img。
   * MutationObserver 只能事后修正，浏览器已可能开始下载。
   * 这里仅对目标 class 的 HTML / img src 写入做预处理。
   */
  if (innerHTMLDescriptor && innerHTMLDescriptor.set) {
    Object.defineProperty(Element.prototype, 'innerHTML', {
      configurable: true,
      enumerable: innerHTMLDescriptor.enumerable,
      get: innerHTMLDescriptor.get,
      set(value) {
        innerHTMLDescriptor.set.call(this, rewriteMarkup(value));
      }
    });
  }

  if (outerHTMLDescriptor && outerHTMLDescriptor.set) {
    Object.defineProperty(Element.prototype, 'outerHTML', {
      configurable: true,
      enumerable: outerHTMLDescriptor.enumerable,
      get: outerHTMLDescriptor.get,
      set(value) {
        outerHTMLDescriptor.set.call(this, rewriteMarkup(value));
      }
    });
  }

  if (nativeCreateContextualFragment) {
    Range.prototype.createContextualFragment = function createContextualFragment(
      markup
    ) {
      return nativeCreateContextualFragment.call(this, rewriteMarkup(markup));
    };
  }

  if (nativeParseFromString) {
    DOMParser.prototype.parseFromString = function parseFromString(
      markup,
      type
    ) {
      const document = nativeParseFromString.call(
        this,
        /html/i.test(type || '') ? rewriteMarkup(markup) : markup,
        type
      );

      if (/html/i.test(type || '')) {
        prepareTree(document);
      }

      return document;
    };
  }

  Document.prototype.write = function write(...markup) {
    return nativeDocumentWrite.apply(this, markup.map(rewriteMarkup));
  };

  Document.prototype.writeln = function writeln(...markup) {
    return nativeDocumentWriteln.apply(this, markup.map(rewriteMarkup));
  };

  Element.prototype.insertAdjacentHTML = function insertAdjacentHTML(
    position,
    text
  ) {
    return nativeInsertAdjacentHTML.call(this, position, rewriteMarkup(text));
  };

  Element.prototype.setAttribute = function setAttribute(name, value) {
    const normalizedName = String(name).toLowerCase();
    let nextValue = value;

    if (
      !isPreparingImage &&
      this instanceof HTMLImageElement &&
      normalizedName === 'src' &&
      shouldOptimizeSourceWrite(this, value) &&
      !shouldSkipSource(value)
    ) {
      const galleryKind = getGalleryKind(this, value);

      nextValue = galleryKind
        ? getGallerySafeSource(this, value, galleryKind)
        : createImageUrl(value, getFallbackWidth(this));
    }

    const result = nativeSetAttribute.call(this, name, nextValue);

    if (
      !isPreparingImage &&
      this instanceof HTMLImageElement &&
      (normalizedName === 'class' ||
        normalizedName === 'src' ||
        normalizedName === 'data-src')
    ) {
      prepareImage(this);
    }

    return result;
  };

  if (imageSrcDescriptor && imageSrcDescriptor.set) {
    Object.defineProperty(HTMLImageElement.prototype, 'src', {
      configurable: true,
      enumerable: imageSrcDescriptor.enumerable,
      get: imageSrcDescriptor.get,
      set(value) {
        if (
          !isPreparingImage &&
          shouldOptimizeSourceWrite(this, value) &&
          !shouldSkipSource(value)
        ) {
          const galleryKind = getGalleryKind(this, value);

          imageSrcDescriptor.set.call(
            this,
            galleryKind
              ? getGallerySafeSource(this, value, galleryKind)
              : createImageUrl(value, getFallbackWidth(this))
          );
          prepareImage(this);
          return;
        }

        imageSrcDescriptor.set.call(this, value);
      }
    });
  }

  Node.prototype.appendChild = function appendChild(node) {
    prepareTreeForParent(this, node);
    return nativeAppendChild.call(this, node);
  };

  Node.prototype.insertBefore = function insertBefore(node, child) {
    prepareTreeForParent(this, node);
    return nativeInsertBefore.call(this, node, child);
  };

  Node.prototype.replaceChild = function replaceChild(node, child) {
    prepareTreeForParent(this, node);
    return nativeReplaceChild.call(this, node, child);
  };

  function optimizeImage(img) {
    if (!(img instanceof HTMLImageElement)) return;

    if (isGalleryImage(img)) {
      prepareGalleryImage(img);
      return;
    }

    const rawSource = getPrimarySource(img);

    if (shouldSkipSource(rawSource)) return;

    /*
     * YMQ 可能重新设置图片地址。
     * 只有当前地址仍是大尺寸时才重新处理。
     */
    const alreadyOptimized =
      img.getAttribute(PROCESSED_ATTR) === 'true' &&
      !rawSource.includes('width=1200') &&
      !rawSource.includes('height=1200');

    if (alreadyOptimized) return;

    prepareImage(img);
  }

  function optimizeAllImages(root = document) {
    if (root instanceof HTMLImageElement && root.matches(SELECTOR)) {
      optimizeImage(root);
      return;
    }

    if (
      !(
        root instanceof Element ||
        root instanceof Document ||
        root instanceof DocumentFragment
      )
    ) {
      return;
    }

    root.querySelectorAll(SELECTOR).forEach(optimizeImage);
  }

  function getGalleryRoots(root) {
    const roots = new Set();

    if (root instanceof Element) {
      if (root.matches(GALLERY_ROOT_SELECTOR)) roots.add(root);

      const closestRoot = root.closest(GALLERY_ROOT_SELECTOR);

      if (closestRoot) roots.add(closestRoot);
    }

    if (
      root instanceof Element ||
      root instanceof Document ||
      root instanceof DocumentFragment
    ) {
      root.querySelectorAll(GALLERY_ROOT_SELECTOR).forEach((galleryRoot) => {
        roots.add(galleryRoot);
      });
    }

    return roots;
  }

  function optimizeGalleryRoots(root = document) {
    getGalleryRoots(root).forEach(optimizeAllImages);
  }

  function scheduleGalleryRefresh(root = document) {
    if (!getGalleryRoots(root).size || isGalleryRefreshQueued) return;

    isGalleryRefreshQueued = true;

    window.requestAnimationFrame(() => {
      isGalleryRefreshQueued = false;
      optimizeGalleryRoots(root);

      window.setTimeout(() => {
        optimizeGalleryRoots(root);
      }, 120);
    });
  }

  /*
   * 立即监听，处理 YMQ 后续动态插入的选项图片。
   */
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (
        mutation.type === 'attributes' &&
        mutation.target instanceof HTMLImageElement
      ) {
        optimizeImage(mutation.target);
        return;
      }

      if (
        mutation.type === 'attributes' &&
        mutation.target instanceof Element
      ) {
        optimizeAllImages(mutation.target);

        const galleryRoot = mutation.target.closest(GALLERY_ROOT_SELECTOR);

        if (galleryRoot) {
          optimizeAllImages(galleryRoot);
          scheduleGalleryRefresh(galleryRoot);
        }

        return;
      }

      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element || node instanceof DocumentFragment) {
          optimizeAllImages(node);
          scheduleGalleryRefresh(node);
        }
      });
    });
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'data-src', 'src']
  });

  function init() {
    optimizeAllImages();

    if (
      window.ymqOptionEvent &&
      typeof window.ymqOptionEvent.on === 'function'
    ) {
      window.ymqOptionEvent.on('afterOptionCreate', () => {
        optimizeAllImages();
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, {
      once: true
    });
  } else {
    init();
  }
})();
